import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { PROPERTIES } from "../../lib/data";
import { MapPin, Maximize2, Tag, ArrowRight } from "lucide-react";

const TYPES = [
  "All",
  "Residential Plot",
  "Commercial",
  "Agricultural Land",
  "Industrial",
];
const CATEGORIES = [
  "All",
  "Investment",
  "High-Yield",
  "Long-Term",
  "Premium",
  "Entry-Level",
];
const BUDGETS = [
  { label: "All", min: 0, max: Infinity },
  { label: "Under ₹30L", min: 0, max: 3000000 },
  { label: "₹30L - 60L", min: 3000000, max: 6000000 },
  { label: "₹60L - 1Cr", min: 6000000, max: 10000000 },
  { label: "Above ₹1Cr", min: 10000000, max: Infinity },
];

export default function Properties({ onOpenLead, compact = false }) {
  const [type, setType] = useState("All");
  const [category, setCategory] = useState("All");
  const [budgetIdx, setBudgetIdx] = useState(0);

  const filtered = useMemo(() => {
    const b = BUDGETS[budgetIdx];
    return PROPERTIES.filter((p) => {
      if (type !== "All" && p.type !== type) return false;
      if (category !== "All" && p.category !== category) return false;
      if (p.priceNumeric < b.min || p.priceNumeric > b.max) return false;
      return true;
    });
  }, [type, category, budgetIdx]);

  const list = compact ? filtered.slice(0, 3) : filtered;

  return (
    <section
      data-testid="properties-section"
      className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4">Properties</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.05]">
              Hand-picked,{" "}
              <span className="gold-gradient-text italic">verified</span>{" "}
              listings.
            </h2>
          </div>
          {!compact && (
            <p className="text-white/60 max-w-md font-light">
              Every listing is RERA-vetted, title-clear and reviewed by our
              senior advisory team before reaching you.
            </p>
          )}
        </div>

        {!compact && (
          <div
            data-testid="property-filters"
            className="glass p-6 md:p-8 mb-12 grid md:grid-cols-3 gap-6"
          >
            <FilterGroup
              label="Type"
              testid="filter-type"
              options={TYPES}
              value={type}
              onChange={setType}
            />
            <FilterGroup
              label="Category"
              testid="filter-category"
              options={CATEGORIES}
              value={category}
              onChange={setCategory}
            />
            <FilterGroup
              label="Budget"
              testid="filter-budget"
              options={BUDGETS.map((b) => b.label)}
              value={BUDGETS[budgetIdx].label}
              onChange={(v) =>
                setBudgetIdx(BUDGETS.findIndex((b) => b.label === v))
              }
            />
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`property-card-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="group glass overflow-hidden hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
                <span className="absolute top-4 left-4 bg-black/60 backdrop-blur-md border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
                  {p.category}
                </span>
              </div>
              <div className="p-7">
                <h3 className="font-serif text-2xl text-white font-light leading-snug">
                  {p.title}
                </h3>
                <div className="mt-3 flex items-center gap-2 text-white/60 text-sm">
                  <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.6} />
                  {p.location}
                </div>
                <p className="mt-4 text-white/55 text-sm font-light leading-relaxed line-clamp-2">
                  {p.description}
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-2 text-white/70">
                    <Maximize2 className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                    {p.area}
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <Tag className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                    {p.type}
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between pt-5 border-t border-white/5">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                      Starting from
                    </div>
                    <div className="font-serif text-xl text-[#D4AF37]">
                      {p.price}
                    </div>
                  </div>
                  <button
                    data-testid={`property-${p.id}-cta`}
                    onClick={onOpenLead}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-white hover:text-[#D4AF37] transition-colors"
                  >
                    Enquire
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {list.length === 0 && (
          <div className="text-center text-white/50 py-20 font-light">
            No properties match your filters. Try widening the budget range.
          </div>
        )}
      </div>
    </section>
  );
}

function FilterGroup({ label, options, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">
        {label}
      </div>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onChange(o)}
            className={`px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-all duration-300 ${
              value === o
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
